/**
 * Tiny fetch wrapper that always attaches the Telegram `initData` header.
 *
 * NEVER skip this header — the backend will reject the request.
 */
// Empty default = call /v1/* on the same origin the frontend was served from.
// In production, nginx proxies /v1/* → backend on 127.0.0.1:8000.
// In local dev, vite's server.proxy in vite.config.ts proxies /v1/* → localhost:8000.
// Either way, no cross-origin requests, no CORS to configure, no env var needed.
// Override only when you have a non-standard setup (e.g. frontend on a different host).
const API_URL = import.meta.env.VITE_API_URL ?? '';

function getInitData(): string {
  const data = window.Telegram?.WebApp?.initData;
  if (data) return data;

  // Local dev shortcut: when Vite is in dev mode AND no real Telegram is
  // running, send the `__DEV__` magic token. The backend (also in dev env)
  // recognises it and returns a hardcoded fake user so you can iterate
  // without a real Mini App URL / tunnel.
  if (import.meta.env.DEV) {
    console.info('[dev] using __DEV__ initData (no real Telegram detected)');
    return '__DEV__';
  }

  console.warn('Telegram initData is empty — are you running inside Telegram?');
  return '';
}

function getLanguage(): 'ru' | 'en' {
  const stored = localStorage.getItem('i18nextLng') ?? '';
  return stored.startsWith('ru') ? 'ru' : 'en';
}

function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-Init-Data', getInitData());
  headers.set('X-Language', getLanguage());
  headers.set('X-Timezone', getTimezone());
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------- Typed endpoints ---------- //

export interface UserRecord {
  id: number;
  first_name: string;
  display_name?: string | null;
  language: 'ru' | 'en';
  birth_date?: string | null;
  main_question?: string | null;
  // Natal-chart inputs — all optional until the user opens the feature.
  birth_time?: string | null;          // "HH:MM" or "HH:MM:SS"
  birth_place?: string | null;
  birth_lat?: number | null;
  birth_lon?: number | null;
  birth_tz_offset?: number | null;     // minutes from UTC
  subscription_tier: 'free' | 'priestess';
  subscription_expires_at?: string | null;
  notifications_enabled?: boolean;
  notification_hour?: number;
}

export interface AuthResponse {
  user: UserRecord;
  needs_onboarding: boolean;
}

export interface ReadingRecord {
  id: string;
  user_id: number;
  kind: string;
  for_date: string;
  cards: unknown[];
  interpretation: string;
  focus_question?: string | null;
  created_at: string;
  /**
   * Present only on the *first* daily-card fetch of the local day.
   * Cached responses don't include it; use getStreak() then.
   */
  streak?: StreakWithRewards | null;
  lunar_event?: 'new_moon' | 'full_moon' | null;
  lunar_phase?: string;
  wisdom?: { text: string; author: string };
  /** Numerology only — true if the report came from cache (no AI call). */
  from_cache?: boolean;
  /** Numerology only — true if the AI returned a short / incomplete report.
   *  In this case the gift was restored and the user can retry for free. */
  truncated?: boolean;
}

export const authWithTelegram = () =>
  request<AuthResponse>('/v1/auth/telegram', { method: 'POST' });

export const getProfile = () => request<UserRecord>('/v1/profile');

export const completeOnboarding = (payload: {
  birth_date: string;
  display_name: string;
  main_question: string;
  language: 'ru' | 'en';
  referrer_id?: number | null;
}) =>
  request<UserRecord>('/v1/profile/onboarding', {
    method: 'POST',
    body: JSON.stringify(payload),
  });


// --- Referrals ---

export interface ReferralTier {
  id: string;
  type: 'spread' | 'subscription';
  icon: string;
  title: string;
  blurb: string;
  day: number;
  unlocked?: boolean;
}

export interface ReferralsState {
  count: number;
  claimed: number[];
  next_tier: ReferralTier | null;
  all_tiers: ReferralTier[];
  share_link: string;
}

export const getReferrals = () => request<ReferralsState>('/v1/profile/referrals');

export const setLanguage = (language: 'ru' | 'en') =>
  request<{ language: 'ru' | 'en'; daily_invalidated: boolean }>(
    '/v1/profile/language',
    { method: 'POST', body: JSON.stringify({ language }) },
  );

export const updateProfile = (payload: {
  display_name?: string;
  main_question?: string;
  birth_date?: string;
  birth_time?: string;
  birth_place?: string;
  birth_lat?: number;
  birth_lon?: number;
  birth_tz_offset?: number;
}) =>
  request<UserRecord>('/v1/profile/me', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });


// --- Natal chart ---

export interface NatalPlace {
  display_name: string;
  lat: number;
  lon: number;
  importance: number;
}

export interface NatalPlanetPlacement {
  planet: 'sun' | 'moon' | 'mercury' | 'venus' | 'mars'
        | 'jupiter' | 'saturn' | 'uranus' | 'neptune' | 'pluto';
  longitude: number;
  sign: 'aries' | 'taurus' | 'gemini' | 'cancer' | 'leo' | 'virgo'
      | 'libra' | 'scorpio' | 'sagittarius' | 'capricorn'
      | 'aquarius' | 'pisces';
  sign_degree: number;
  house: number;
  retrograde: boolean;
}

export interface NatalHouseCusp {
  house: number;
  longitude: number;
  sign: NatalPlanetPlacement['sign'];
  sign_degree: number;
}

export interface NatalAspect {
  planet_a: NatalPlanetPlacement['planet'];
  planet_b: NatalPlanetPlacement['planet'];
  aspect: 'conjunction' | 'opposition' | 'trine' | 'square' | 'sextile';
  orb: number;
  applying: boolean;
}

export interface NatalChartData {
  birth_utc: string;
  lat: number;
  lon: number;
  planets: NatalPlanetPlacement[];
  houses: NatalHouseCusp[];          // length 12
  ascendant: number;
  midheaven: number;
  aspects: NatalAspect[];
  dominant_element: 'fire' | 'earth' | 'air' | 'water';
  dominant_modality: 'cardinal' | 'fixed' | 'mutable';
}

export interface NatalReading {
  id: string | null;                  // null when truncated
  kind: 'natal_chart';
  interpretation: string;
  metadata: NatalChartData;
  cards: never[];
  for_date: string;
  focus_question: string;             // hash of inputs (cache key)
  from_cache?: boolean;
  truncated?: boolean;
}

export const searchPlace = (q: string) =>
  request<{ places: NatalPlace[] }>(
    `/v1/natal/search-place?q=${encodeURIComponent(q)}`,
  );

export const computeNatal = (payload: {
  birth_time?: string;
  birth_place?: string;
  birth_lat?: number;
  birth_lon?: number;
  birth_tz_offset_minutes?: number;
}) =>
  request<NatalReading>('/v1/natal/compute', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/** Trigger PDF generation and delivery to the user's Telegram chat
 *  via the bot. The backend renders the PDF, sends it as a document
 *  to the user's private chat with the bot, and returns a JSON ack
 *  with the filename. The actual file lives in Telegram, NOT as a
 *  blob this client downloads.
 *
 *  Why this UX, not <a download>: iOS Telegram WebView silently fails
 *  to save files via the download attribute; the bot-message channel
 *  works on every platform Telegram supports. */
export const requestNatalPdf = () =>
  request<{ delivered: 'telegram_chat'; filename: string }>(
    '/v1/natal/pdf',
  );


// --- Synastry ---

export interface SynastryCrossAspect {
  planet_a: NatalPlanetPlacement['planet'];
  planet_b: NatalPlanetPlacement['planet'];
  aspect: 'conjunction' | 'opposition' | 'trine' | 'square' | 'sextile';
  orb: number;
  a_sign: NatalPlanetPlacement['sign'];
  b_sign: NatalPlanetPlacement['sign'];
}

export interface SynastryOverlay {
  aspects: SynastryCrossAspect[];
  harmonious_count: number;
  challenging_count: number;
  neutral_count: number;
}

export interface SynastryReading {
  id: string | null;
  kind: 'synastry';
  interpretation: string;
  metadata: {
    chart_a: NatalChartData;
    chart_b: NatalChartData;
    overlay: SynastryOverlay;
    partner_name: string;
    partner_birth_place: string;
  };
  cards: never[];
  for_date: string;
  focus_question: string;
  from_cache?: boolean;
  truncated?: boolean;
}

export const computeSynastry = (payload: {
  partner_name: string;
  partner_birth_date: string;         // YYYY-MM-DD
  partner_birth_time: string;         // HH:MM
  partner_birth_place: string;
  partner_birth_lat: number;
  partner_birth_lon: number;
  partner_birth_tz_offset_minutes: number;
}) =>
  request<SynastryReading>('/v1/synastry/compute', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const getGifts = () =>
  request<{ products: string[] }>('/v1/profile/gifts');

export const getChannelLink = () =>
  request<{ url: string | null }>('/v1/profile/channel');

export const updateNotifications = (payload: { enabled: boolean; hour: number }) =>
  request<{ enabled: boolean; hour: number }>('/v1/profile/notifications', {
    method: 'POST',
    body: JSON.stringify(payload),
  });


// --- Streaks & rewards ---

export interface StreakReward {
  id: string;
  type: 'badge' | 'spread' | 'subscription';
  icon: string;
  title: string;
  blurb: string;
  day?: number;        // present on next_reward / all_rewards
  days_left?: number;  // present on next_reward
}

export interface StreakState {
  current: number;
  longest: number;
  achievements: string[];
  next_milestone: number | null;
  next_reward: StreakReward | null;
  all_rewards?: StreakReward[];   // only present on getStreak() response
}

export interface StreakWithRewards extends StreakState {
  new_rewards: StreakReward[];
}

export const getStreak = () => request<StreakState>('/v1/profile/streak');


// --- Lunar / rituals ---

export interface LunarPhase {
  key: string;
  name_ru: string;
  name_en: string;
  illumination: number;
  waxing: boolean;
  event: 'new_moon' | 'full_moon' | 'first_quarter' | 'last_quarter' | null;
  fraction: number;
}

export interface LunarEvent {
  date: string;        // ISO yyyy-mm-dd
  type: 'new_moon' | 'full_moon';
}

export interface AstroEvent {
  date_start: string;
  date_end: string | null;
  category: 'retrograde' | 'eclipse' | 'turning' | 'shower' | 'transit';
  icon: string;
  title_ru: string;
  title_en: string;
  body_ru: string;
  body_en: string;
}

export interface LunarState {
  phase: LunarPhase;
  ritual: { title: string; body: string };
  upcoming: LunarEvent[];
  /** Priestess-only daily do/don't lists. null for free users. */
  day_advice: { good: string[]; avoid: string[] } | null;
  astro_events: AstroEvent[];
}

export const getLunar = () => request<LunarState>('/v1/profile/lunar');

export const getDailyCard = () => request<ReadingRecord>('/v1/readings/daily');

export const getWeeklyCard = () => request<ReadingRecord>('/v1/readings/weekly');

export const listReadings = (limit = 40) =>
  request<ReadingRecord[]>(`/v1/readings/?limit=${limit}`);


export interface ReadingStats {
  total: number;
  by_kind: Record<string, number>;
  by_date: Record<string, number>;
  top_cards: Array<{ name_en: string; name_ru: string; count: number }>;
}

export const getReadingStats = () =>
  request<ReadingStats>('/v1/readings/stats');

export const deleteReading = (id: string) =>
  request<{ ok: true; deleted: string }>(`/v1/readings/${id}`, {
    method: 'DELETE',
  });

export type SpreadType =
  | 'celtic_cross' | 'three_card' | 'love'
  | 'horseshoe' | 'magus_star' | 'wedding'
  | 'home' | 'masks' | 'wallet' | 'shadow' | 'crossroads';

export const requestSpread = (payload: {
  spread_type: SpreadType;
  focus_question?: string;
}) =>
  request<ReadingRecord>('/v1/readings/spread', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const requestDreamReading = (payload: { dream_text: string }) =>
  request<ReadingRecord>('/v1/readings/dream', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const requestNumerologyReport = (payload: { full_name?: string }) =>
  request<ReadingRecord>('/v1/readings/numerology', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export type ThemedTheme = 'love' | 'work' | 'money' | 'health' | 'spiritual';
export const regenerateReading = (readingId: string) =>
  request<ReadingRecord>(`/v1/readings/${readingId}/regenerate`, {
    method: 'POST',
  });

export const requestThemedReading = (payload: {
  theme: ThemedTheme;
  question?: string;
}) =>
  request<ReadingRecord>('/v1/readings/themed', {
    method: 'POST',
    body: JSON.stringify(payload),
  });


// --- Yes/No (free, instant) ---

export interface YesNoResult {
  card: {
    arcana: 'major' | 'minor';
    name_en: string;
    name_ru: string;
    keywords_en: string[];
    keywords_ru: string[];
    orientation: 'upright' | 'reversed';
  };
  answer: 'yes' | 'no' | 'maybe';
  message: string;
  question: string | null;
  share_url: string;
}

export const requestYesNo = (question?: string) =>
  request<YesNoResult>('/v1/readings/yes_no', {
    method: 'POST',
    body: JSON.stringify({ question: question ?? null }),
  });


// --- Name compatibility (free, instant, viral) ---

export interface NameCompatResult {
  name_a: string;
  name_b: string;
  number_a: number;
  number_b: number;
  score: number;
  verdict: 'resonant' | 'harmonious' | 'neutral' | 'challenging';
  blurb: string;
  share_url: string;
}

export const requestNameCompat = (name_a: string, name_b: string) =>
  request<NameCompatResult>('/v1/readings/name_compat', {
    method: 'POST',
    body: JSON.stringify({ name_a, name_b }),
  });


// --- Angel numbers (free, hybrid template+AI) ---

export interface AngelResult {
  number: string;
  meaning: string;
  cue: string;
  source: 'template' | 'ai';
  share_url: string;
}

export const requestAngelNumber = (number: string) =>
  request<AngelResult>('/v1/readings/angel', {
    method: 'POST',
    body: JSON.stringify({ number }),
  });


// --- AI Journal ---

export interface JournalEntry {
  id: string;
  for_date: string;
  interpretation: string;
  created_at?: string;
}

export interface JournalInsight {
  id: string | null;
  for_date: string;
  kind: string;
  interpretation: string;
  stub?: boolean;
}

export const addJournalEntry = (text: string) =>
  request<JournalEntry>('/v1/journal/entry', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });

export const listJournalEntries = (limit = 30) =>
  request<JournalEntry[]>(`/v1/journal?limit=${limit}`);

export const deleteJournalEntry = (id: string) =>
  request<{ ok: true; deleted: string }>(`/v1/journal/${id}`, {
    method: 'DELETE',
  });

export const getJournalInsight = () =>
  request<JournalInsight>('/v1/journal/insight');

export const createInvoice = (product_id: string) =>
  request<{ invoice_link: string; product: { title: string; stars: number } }>(
    '/v1/payments/invoice',
    { method: 'POST', body: JSON.stringify({ product_id }) },
  );


// --- Compatibility ---

export interface CompatBasic {
  mental: number;
  emotional: number;
  average: number;
  me_sign: string;
  other_sign: string;
  teaser: string;
}

export const compatibilityCheck = (payload: {
  other_name: string;
  other_birth_date: string;
  full?: boolean;
}) =>
  request<{ basic: CompatBasic; full: string | null }>(
    '/v1/readings/compatibility',
    { method: 'POST', body: JSON.stringify(payload) },
  );

export const getCompatInvite = (inviteId: string) =>
  request<{
    inviter_display_name: string;
    inviter_sign: string;
    language: string;
  }>(`/v1/compat/invite/${inviteId}`);

export const createCompatInvite = () =>
  request<{ invite_id: string; share_link: string }>('/v1/compat/invite', {
    method: 'POST',
  });

export const acceptCompatInvite = (
  inviteId: string,
  payload: { invitee_name: string; invitee_birth: string },
) =>
  request<{ basic: CompatBasic; invite_id: string }>(
    `/v1/compat/invite/${inviteId}/accept`,
    { method: 'POST', body: JSON.stringify(payload) },
  );


// --- Master's room (chat) ---

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  ts?: string;
}

export const masterChat = (history: ChatMessage[], userMessage: string) =>
  request<{ reply: string }>(
    '/v1/master/chat',
    { method: 'POST', body: JSON.stringify({ history, message: userMessage }) },
  );


export interface ErrorReportPayload {
  screen: string;
  message: string;
  context?: Record<string, string | number | boolean | null>;
}

export const reportError = (payload: ErrorReportPayload) =>
  request<{ ok: true }>('/v1/feedback/error_report', {
    method: 'POST',
    body: JSON.stringify(payload),
  });


// ─── Admin / analytics ───────────────────────────────────────────────────

export interface AdminStatsResponse {
  current: {
    captured_at: string;
    users: {
      total: number; onboarded: number;
      dau: number; wau: number; mau: number;
      notifications_opted_in: number;
    };
    subscription: { priestess_active: number; priestess_expiring_7d: number };
    revenue_stars: { h24: number; d7: number; d30: number; total: number };
    streaks: { above_7: number; above_30: number };
    referrals: { total: number; converted: number };
    top_spreads_7d: Array<{ kind: string; count: number }>;
    activity_by_hour_7d: number[];
  };
  history: Array<Record<string, number | string>>;
}

export const getAdminStats = (token: string, historyDays = 30) =>
  fetch(`${API_URL}/v1/admin/stats?history_days=${historyDays}`, {
    headers: { 'X-Admin-Token': token },
  }).then(async (r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return (await r.json()) as AdminStatsResponse;
  });


// AI cost analytics ─ separate endpoint, queried with the same admin token.
// Aggregations are computed live from `ai_usage` (no snapshot table).
export interface AdminCostResponse {
  window_days: number;
  captured_at: string;
  totals: {
    cost_usd: number;
    cost_24h_usd: number;
    cost_7d_usd: number;
    tokens_in: number;
    tokens_out: number;
    users_active: number;
    avg_cost_per_user_usd: number;
  };
  by_kind: Array<{
    kind: string;
    cost_usd: number;
    tokens: number;
    calls: number;
  }>;
  top_users: Array<{
    user_id: number;
    display_name: string;
    cost_usd: number;
    calls: number;
    priestess: boolean;
  }>;
  daily_series: Array<{ day: string; cost_usd: number }>;
}

export const getAdminCost = (token: string, days = 30) =>
  fetch(`${API_URL}/v1/admin/cost?days=${days}`, {
    headers: { 'X-Admin-Token': token },
  }).then(async (r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return (await r.json()) as AdminCostResponse;
  });
